const normalizeDeviceKey = (value) => String(value || '').toLowerCase().replace(/\s+/g, '');

const parsePxValue = (value) => {
    const parsed = Number.parseFloat(String(value || '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const stripUnit = (value, unit) => {
    const raw = String(value || '').trim();
    if (!unit) return raw;
    return raw.endsWith(unit) ? raw.slice(0, -unit.length) : raw;
};

const getRuleAtRuleText = (rule) => {
    if (!rule) return '';
    if (typeof rule.getAtRule === 'function') {
        return rule.getAtRule() || '';
    }
    if (typeof rule.get === 'function') {
        return rule.get('atRule') || rule.get('mediaText') || '';
    }
    return '';
};

const getRuleWidth = (rule) => {
    const atRule = getRuleAtRuleText(rule);
    if (!atRule) return Number.POSITIVE_INFINITY;
    const maxWidthMatch = atRule.match(/max-width\s*:\s*([0-9.]+)px/i);
    if (!maxWidthMatch) return null;
    return Number.parseFloat(maxWidthMatch[1]);
};

export const createStyleSyncService = (editor) => {
    const getStyleTarget = (component) => {
        const selected = typeof editor.getSelected === 'function' ? editor.getSelected() : null;
        if (selected === component) {
            if (typeof editor.getSelectedToStyle === 'function') {
                const selectedToStyle = editor.getSelectedToStyle();
                if (selectedToStyle) return selectedToStyle;
            }
            if (editor.StyleManager && typeof editor.StyleManager.getSelected === 'function') {
                const styleManagerSelected = editor.StyleManager.getSelected();
                if (styleManagerSelected) return styleManagerSelected;
            }
        }

        return component;
    };

    const getSelectorForComponent = (component) => {
        const selectedToStyle = typeof editor.getSelectedToStyle === 'function' ? editor.getSelectedToStyle() : null;
        if (selectedToStyle && typeof selectedToStyle.getSelectorsString === 'function') {
            const selector = selectedToStyle.getSelectorsString();
            if (selector) return selector.trim();
        }

        if (editor.StyleManager && typeof editor.StyleManager.getSelected === 'function') {
            const selected = editor.StyleManager.getSelected();
            if (selected && typeof selected.getSelectorsString === 'function') {
                const selector = selected.getSelectorsString();
                if (selector) return selector.trim();
            }
        }

        const target = getStyleTarget(component);
        if (target && typeof target.getSelectorsString === 'function') {
            const selector = target.getSelectorsString();
            if (selector) return selector.trim();
        }

        if (component && typeof component.getId === 'function') {
            const id = component.getId();
            if (id) return `#${id}`;
        }

        return '';
    };

    const getFallbackDeviceWidths = () => {
        const deviceManager = editor.DeviceManager;
        if (!deviceManager || typeof deviceManager.getAll !== 'function') {
            return [Number.POSITIVE_INFINITY];
        }

        const allDevices = deviceManager.getAll();
        const devicesList = allDevices && typeof allDevices.map === 'function'
            ? allDevices.map((device) => device)
            : (Array.isArray(allDevices) ? allDevices : []);
        const mapped = devicesList.map((device) => {
            const name = device?.get ? device.get('name') : device?.name;
            const id = device?.get ? device.get('id') : device?.id;
            const widthMedia = device?.get ? device.get('widthMedia') : device?.widthMedia;
            const width = device?.get ? device.get('width') : device?.width;
            const widthPx = parsePxValue(widthMedia || width);
            return {
                name,
                id,
                widthPx,
            };
        }).sort((a, b) => a.widthPx - b.widthPx);

        const currentDevice = normalizeDeviceKey(typeof editor.getDevice === 'function' ? editor.getDevice() : '');
        const current = mapped.find((device) => {
            return normalizeDeviceKey(device.name) === currentDevice || normalizeDeviceKey(device.id) === currentDevice;
        });

        const currentWidth = current ? current.widthPx : Number.POSITIVE_INFINITY;
        const fallbackWidths = mapped
            .filter((device) => device.widthPx >= currentWidth)
            .map((device) => device.widthPx);

        if (!fallbackWidths.includes(Number.POSITIVE_INFINITY)) {
            fallbackWidths.push(Number.POSITIVE_INFINITY);
        }

        return [...new Set(fallbackWidths)];
    };

    const getPropertyFromCssRules = ({ selector, property, widthPx }) => {
        if (!selector || !editor.CssComposer || typeof editor.CssComposer.getAll !== 'function') {
            return '';
        }

        const rules = editor.CssComposer.getAll();
        let foundValue = '';
        rules.forEach((rule) => {
            const ruleSelector = typeof rule.getSelectorsString === 'function' ? (rule.getSelectorsString() || '').trim() : '';
            if (ruleSelector !== selector) return;

            const ruleWidth = getRuleWidth(rule);
            if (ruleWidth === null) return;

            const isMatchingWidth = Number.isFinite(widthPx)
                ? Number.isFinite(ruleWidth) && Math.abs(ruleWidth - widthPx) < 0.5
                : !Number.isFinite(ruleWidth);

            if (!isMatchingWidth) return;

            const ruleStyle = typeof rule.getStyle === 'function' ? rule.getStyle() : {};
            const ruleValue = ruleStyle?.[property];
            if (ruleValue) {
                foundValue = ruleValue;
            }
        });

        return foundValue;
    };

    const readPropertyValue = ({ component, property, unit }) => {
        const styleTarget = getStyleTarget(component);
        const targetStyle = styleTarget && typeof styleTarget.getStyle === 'function' ? styleTarget.getStyle() : {};
        const targetValue = targetStyle?.[property];
        if (targetValue) return stripUnit(targetValue, unit);

        const selected = typeof editor.getSelected === 'function' ? editor.getSelected() : null;
        if (selected === component) {
            const selector = getSelectorForComponent(component);
            const fallbackWidths = getFallbackDeviceWidths();

            for (const widthPx of fallbackWidths) {
                const fallbackValue = getPropertyFromCssRules({ selector, property, widthPx });
                if (fallbackValue) {
                    return stripUnit(fallbackValue, unit);
                }
            }
        }

        const componentStyle = component && typeof component.getStyle === 'function' ? component.getStyle() : {};
        return stripUnit(componentStyle[property] || '', unit);
    };

    const applyStyleValue = ({ component, property, value }) => {
        const styleValue = { [property]: value };
        const selected = typeof editor.getSelected === 'function' ? editor.getSelected() : null;
        const canUseStyleManager = selected === component
            && editor.StyleManager
            && typeof editor.StyleManager.addStyleTargets === 'function';

        if (canUseStyleManager) {
            editor.StyleManager.addStyleTargets(styleValue);
            return;
        }

        const styleTarget = getStyleTarget(component);
        if (styleTarget && typeof styleTarget.addStyle === 'function') {
            styleTarget.addStyle(styleValue);
            return;
        }

        component.addStyle(styleValue);
    };

    const subscribeToDeviceChanges = (handler) => {
        if (typeof handler !== 'function' || typeof editor.on !== 'function') {
            return () => {};
        }

        editor.on('change:device', handler);
        editor.on('device:select', handler);

        return () => {
            if (typeof editor.off !== 'function') return;
            editor.off('change:device', handler);
            editor.off('device:select', handler);
        };
    };

    const subscribeToDeviceChangesForElement = ({ element, onChange }) => {
        if (!element || typeof onChange !== 'function') {
            return () => {};
        }

        let unsubscribe = () => {};
        const wrappedHandler = () => {
            if (!element.isConnected) {
                unsubscribe();
                unsubscribe = () => {};
                return;
            }
            onChange();
        };

        unsubscribe = subscribeToDeviceChanges(wrappedHandler);
        return () => {
            unsubscribe();
            unsubscribe = () => {};
        };
    };

    return {
        applyStyleValue,
        getStyleTarget,
        readPropertyValue,
        subscribeToDeviceChanges,
        subscribeToDeviceChangesForElement,
    };
};

export default createStyleSyncService;
